import { joiResolver } from "@hookform/resolvers/joi";
import { signInWithRedirect } from "aws-amplify/auth";
import { RhfInput } from "components/rhfs/RhfInput";
import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import Joi from "joi";
import type { FC } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { SignInInput } from "routes/SignInPage";

type SignInPageProps = {
  onSignIn: SubmitHandler<SignInInput>;
};

export const SignInPage: FC<SignInPageProps> = ({ onSignIn }) => {
  const schema = Joi.object({
    email: Joi.string().required().messages({
      "string.empty": "メールアドレスは必須項目です",
    }),
    password: Joi.string().required().messages({
      "string.empty": "パスワードは必須項目です",
    }),
  });
  const methods = useForm<SignInInput>({
    resolver: joiResolver(schema),
  });
  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-4 py-8">
      {/* Logo Section */}
      <div className="mb-4">
        <img
          className="w-full max-w-md h-auto drop-shadow-lg -translate-x-4"
          src={`${process.env.PUBLIC_URL}/images/strix_search_top_logo1.png`}
          alt="Strix Search"
        />
      </div>

      {/* Form Section */}
      <div className="w-full max-w-sm">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSignIn)}>
            <div className="card shadow-2xl bg-white/80 backdrop-blur-sm border border-gray-200/50">
              <div className="card-body">
                <h2 className="text-center text-lg font-semibold text-gray-700 mb-4">
                  ログイン
                </h2>
                <RhfInput
                  label="メールアドレス"
                  name="email"
                  placeholder="メールアドレスを入力してください"
                  data-testid="signin-page-username"
                />
                <RhfInput
                  label="パスワード"
                  name="password"
                  type="password"
                  placeholder="パスワードを入力してください"
                  data-testid="signin-page-password"
                />
                <div className="form-control mt-6 mb-1.5">
                  <Button
                    type="submit"
                    data-testid="signin-page-signin"
                    width="full"
                  >
                    ログイン
                  </Button>
                </div>
                <div className="divider text-xs text-gray-400">または</div>
                {/* Google 公式ブランディングの白ボタンは Button リーフの variant に
                    乗らないため、ここだけ生 className を許容する */}
                <button
                  type="button"
                  // eslint-disable-next-line no-restricted-syntax
                  className="btn w-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium text-sm gap-3"
                  onClick={() => signInWithRedirect({ provider: "Google" })}
                  data-testid="signin-page-google"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 48 48"
                    className="w-5 h-5"
                  >
                    <path
                      fill="#EA4335"
                      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                    />
                    <path
                      fill="#4285F4"
                      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                    />
                    <path
                      fill="#34A853"
                      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                    />
                  </svg>
                  Google でログイン
                </button>
                <p className="text-right text-xs label-text-alt mt-4">
                  パスワードをお忘れの方は
                  <Link
                    to="/reset-pass-word"
                    data-testid="signin-page-to-reset-pass-word"
                  >
                    こちら
                  </Link>
                </p>
                <p className="text-right text-xs label-text-alt">
                  新規登録は
                  <Link to="/signup" data-testid="signin-page-to-signup">
                    こちら
                  </Link>
                </p>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};
